import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";
import { Building, Database, Trash2 } from "lucide-react";

const DEMO_ORG_ID = "a1b2c3d4-e5f6-7890-abcd-ef1234567890";

export const OrganizationTab = () => {
  const { organizationId } = useOrganizationContext();
  const [orgName, setOrgName] = useState("");
  const [orgCode, setOrgCode] = useState("");
  const [orgLogo, setOrgLogo] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    setLoading(true);
    supabase.from("organizations").select("name, code, logo_url").eq("id", organizationId).single()
      .then(({ data }) => {
        if (data) { setOrgName(data.name || ""); setOrgCode(data.code || ""); setOrgLogo(data.logo_url || ""); }
        setLoading(false);
      });
  }, [organizationId]);

  const handleSave = async () => {
    if (!organizationId) return;
    setSaving(true);
    const { error } = await supabase.from("organizations").update({
      name: orgName.trim(), code: orgCode.trim() || null, logo_url: orgLogo.trim() || null,
    }).eq("id", organizationId);
    setSaving(false);
    if (error) {
      toast({ title: "Error", description: "Failed to update organization", variant: "destructive" });
    } else {
      toast({ title: "Saved", description: "Organization details updated successfully." });
    }
  };

  const handleDemoData = async (action: "populate" | "clear") => {
    const setter = action === "populate" ? setPopulating : setClearing;
    setter(true);
    try {
      const { data, error } = await supabase.functions.invoke("populate-demo-data", {
        body: { organization_id: organizationId, branch_id: 100, action },
      });
      if (error) throw error;
      toast({
        title: action === "populate" ? "Demo Data Populated" : "Demo Data Cleared",
        description: data?.message || "Done",
      });
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to process demo data", variant: "destructive" });
    } finally {
      setter(false);
    }
  };

  if (loading) return <div className="text-muted-foreground text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Organization Details</CardTitle>
          </div>
          <CardDescription>Manage your organization's information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input id="org-name" value={orgName} onChange={e => setOrgName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-code">Organization Code</Label>
              <Input id="org-code" value={orgCode} onChange={e => setOrgCode(e.target.value)} placeholder="e.g. ORG001" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="org-logo">Logo URL</Label>
            <Input id="org-logo" value={orgLogo} onChange={e => setOrgLogo(e.target.value)} placeholder="https://..." />
          </div>
          {orgLogo && (
            <div className="flex items-center gap-4">
              <img src={orgLogo} alt="Organization Logo" className="h-16 w-16 object-contain rounded border border-border" />
              <span className="text-sm text-muted-foreground">Logo preview</span>
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Button>
          </div>
        </CardContent>
      </Card>

      {organizationId === DEMO_ORG_ID && (
        <Card className="border-dashed border-primary/40">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Demo Data Management</CardTitle>
            </div>
            <CardDescription>
              Populate realistic patient data for today to showcase dashboard KPIs — includes 15 patients at various workflow stages, billing states, and escalation alerts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button onClick={() => handleDemoData("populate")} disabled={populating || clearing}>
                <Database className="h-4 w-4 mr-1" />
                {populating ? "Populating..." : "Populate Demo Data"}
              </Button>
              <Button variant="outline" onClick={() => handleDemoData("clear")} disabled={populating || clearing}>
                <Trash2 className="h-4 w-4 mr-1" />
                {clearing ? "Clearing..." : "Clear Demo Data"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
